import { useState, useEffect } from "react";
import LeslieFooter from "./LeslieFooter.js";
import VisitedPagesMenu from "./VisitedPagesMenu.js";
import { useSubdomain } from "./SubdomainProvider.js";
import yellowMan from "../assets/pictures/stickfigureyellowwalk.png";

/** Merge saved pool with wardrobe initial order; used slots stay as { letter: null, id }. */
function normalizeAvailableLetters(saved, initial) {
  if (!initial || initial.length === 0) return [];
  const byId = new Map((saved || []).map((s) => [s.id, s]));
  return initial.map((init) => {
    const found = byId.get(init.id);
    if (found && found.letter != null && found.letter !== "") {
      return { letter: found.letter, id: init.id };
    }
    return { letter: null, id: init.id };
  });
}

/** App subdomain — four fixed configs (jeans / shorts × no Seattle word). */
const marbleAppJeansWords = {
  swan: "_W__",
  necklace: "__C__AC_",
  marble: "M___LE",
  danger: "D____R",
  signal: "S____L",
  vui: "V__",
  ve: "V_",
  cup: "__P",
  blue: "___E",
  jeans: "J__NS",
  black: "_____",
  hat: "_AT",
};
const marbleAppShortsWords = {
  swan: "_W__",
  necklace: "__C__AC_",
  marble: "M___LE",
  danger: "D____R",
  signal: "S____L",
  vui: "V__",
  ve: "V_",
  cup: "__P",
  shorts: "S__R__",
  black: "_____",
  hat: "_AT",
};
const marbleAppJeansLetters = [
  { letter: "R", id: 0 },
  { letter: "E", id: 1 },
  { letter: "U", id: 2 },
  { letter: "G", id: 3 },
  { letter: "E", id: 4 },
  { letter: "N", id: 5 },
  { letter: "K", id: 6 },
  { letter: "U", id: 7 },
  { letter: "L", id: 8 },
  { letter: "A", id: 9 },
  { letter: "E", id: 10 },
  { letter: "A", id: 11 },
  { letter: "S", id: 12 },
  { letter: "N", id: 13 },
  { letter: "A", id: 14 },
  { letter: "B", id: 15 },
  { letter: "I", id: 16 },
  { letter: "E", id: 17 },
  { letter: "K", id: 18 },
  { letter: "U", id: 19 },
  { letter: "A", id: 20 },
  { letter: "G", id: 21 },
  { letter: "B", id: 22 },
  { letter: "N", id: 23 },
  { letter: "H", id: 24 },
  { letter: "B", id: 25 },
  { letter: "A", id: 26 },
  { letter: "C", id: 27 },
  { letter: "I", id: 28 },
  { letter: "A", id: 29 },
  { letter: "L", id: 30 },
  { letter: "C", id: 31 },
  { letter: "N", id: 32 },
  { letter: "E", id: 33 },
  { letter: "L", id: 34 },
];
const marbleAppShortsLetters = [
  { letter: "R", id: 0 },
  { letter: "E", id: 1 },
  { letter: "U", id: 2 },
  { letter: "G", id: 3 },
  { letter: "E", id: 4 },
  { letter: "N", id: 5 },
  { letter: "K", id: 6 },
  { letter: "U", id: 7 },
  { letter: "L", id: 8 },
  { letter: "A", id: 9 },
  { letter: "E", id: 10 },
  { letter: "A", id: 11 },
  { letter: "S", id: 12 },
  { letter: "N", id: 13 },
  { letter: "A", id: 14 },
  { letter: "B", id: 15 },
  { letter: "I", id: 16 },
  { letter: "E", id: 17 },
  { letter: "K", id: 18 },
  { letter: "H", id: 19 },
  { letter: "A", id: 20 },
  { letter: "G", id: 21 },
  { letter: "B", id: 22 },
  { letter: "N", id: 23 },
  { letter: "H", id: 24 },
  { letter: "O", id: 25 },
  { letter: "A", id: 26 },
  { letter: "C", id: 27 },
  { letter: "I", id: 28 },
  { letter: "T", id: 29 },
  { letter: "L", id: 30 },
  { letter: "C", id: 31 },
  { letter: "N", id: 32 },
  { letter: "S", id: 33 },
];

/** Seattle subdomain — VUI+VE replaced by starbucks word; explicit letter pools. */
const marbleSeattleJeansWords = {
  swan: "_W__",
  necklace: "__C__AC_",
  marble: "M___LE",
  danger: "D____R",
  signal: "S____L",
  starbucks: "_-B_C__",
  cup: "__P",
  blue: "___E",
  jeans: "J__NS",
  black: "_____",
  hat: "_AT",
};
const marbleSeattleShortsWords = {
  swan: "_W__",
  necklace: "__C__AC_",
  marble: "M___LE",
  danger: "D____R",
  signal: "S____L",
  starbucks: "_-B_C__",
  cup: "__P",
  shorts: "S__R__",
  black: "_____",
  hat: "_AT",
};
const marbleSeattleJeansLetters = [
  { letter: "R", id: 0 },
  { letter: "G", id: 1 },
  { letter: "E", id: 2 },
  { letter: "N", id: 3 },
  { letter: "K", id: 4 },
  { letter: "U", id: 5 },
  { letter: "L", id: 6 },
  { letter: "A", id: 7 },
  { letter: "E", id: 8 },
  { letter: "A", id: 9 },
  { letter: "U", id: 10 },
  { letter: "S", id: 11 },
  { letter: "N", id: 12 },
  { letter: "A", id: 13 },
  { letter: "B", id: 14 },
  { letter: "E", id: 15 },
  { letter: "K", id: 16 },
  { letter: "U", id: 17 },
  { letter: "A", id: 18 },
  { letter: "G", id: 19 },
  { letter: "B", id: 20 },
  { letter: "K", id: 21 },
  { letter: "N", id: 22 },
  { letter: "H", id: 23 },
  { letter: "B", id: 24 },
  { letter: "A", id: 25 },
  { letter: "★", id: 26 },
  { letter: "C", id: 27 },
  { letter: "I", id: 28 },
  { letter: "A", id: 29 },
  { letter: "L", id: 30 },
  { letter: "C", id: 31 },
  { letter: "S", id: 32 },
  { letter: "N", id: 33 },
  { letter: "E", id: 34 },
  { letter: "L", id: 35 },
];
const marbleSeattleShortsLetters = [
  { letter: "R", id: 0 },
  { letter: "G", id: 1 },
  { letter: "E", id: 2 },
  { letter: "N", id: 3 },
  { letter: "K", id: 4 },
  { letter: "U", id: 5 },
  { letter: "L", id: 6 },
  { letter: "A", id: 7 },
  { letter: "K", id: 8 },
  { letter: "E", id: 9 },
  { letter: "A", id: 10 },
  { letter: "S", id: 11 },
  { letter: "N", id: 12 },
  { letter: "A", id: 13 },
  { letter: "B", id: 14 },
  { letter: "★", id: 15 },
  { letter: "E", id: 16 },
  { letter: "K", id: 17 },
  { letter: "H", id: 18 },
  { letter: "A", id: 19 },
  { letter: "G", id: 20 },
  { letter: "B", id: 21 },
  { letter: "N", id: 22 },
  { letter: "U", id: 23 },
  { letter: "H", id: 24 },
  { letter: "O", id: 25 },
  { letter: "A", id: 26 },
  { letter: "C", id: 27 },
  { letter: "S", id: 28 },
  { letter: "I", id: 29 },
  { letter: "T", id: 30 },
  { letter: "L", id: 31 },
  { letter: "C", id: 32 },
  { letter: "N", id: 33 },
  { letter: "S", id: 34 },
];

const marbleInitialLetterPoolsBySubdomain = {
  seattle: [marbleSeattleJeansLetters, marbleSeattleShortsLetters],
  app: [marbleAppJeansLetters, marbleAppShortsLetters],
};
const marbleInitialWordSetsBySubdomain = {
  seattle: [marbleSeattleJeansWords, marbleSeattleShortsWords],
  app: [marbleAppJeansWords, marbleAppShortsWords],
};

function getMarbleInitialPuzzle(subdomain, wardrobe) {
  const jeans = wardrobe !== "Shorts";
  if (subdomain === "seattle") {
    return jeans
      ? { words: marbleSeattleJeansWords, letters: marbleSeattleJeansLetters }
      : { words: marbleSeattleShortsWords, letters: marbleSeattleShortsLetters };
  }
  return jeans
    ? { words: marbleAppJeansWords, letters: marbleAppJeansLetters }
    : { words: marbleAppShortsWords, letters: marbleAppShortsLetters };
}

function marbleSavedStateMatchesSubdomain(savedPuzzleWords, subdomain) {
  if (!savedPuzzleWords) return false;
  return subdomain === "seattle"
    ? "starbucks" in savedPuzzleWords && !("vui" in savedPuzzleWords)
    : "vui" in savedPuzzleWords &&
        "ve" in savedPuzzleWords &&
        !("starbucks" in savedPuzzleWords);
}

function getMarbleStorageKeys(subdomain) {
  const suffix = subdomain === "seattle" ? "seattle" : "app";
  return {
    puzzleWords: `puzzleWords:${suffix}`,
    availableLetters: `availableLetters:${suffix}`,
  };
}

function loadMarbleSavedState(subdomain) {
  const keys = getMarbleStorageKeys(subdomain);
  let puzzleWordsStr = localStorage.getItem(keys.puzzleWords);
  let availableLettersStr = localStorage.getItem(keys.availableLetters);

  if (!puzzleWordsStr || !availableLettersStr) {
    const legacyWordsStr = localStorage.getItem("puzzleWords");
    const legacyLettersStr = localStorage.getItem("availableLetters");
    if (legacyWordsStr && legacyLettersStr) {
      const legacyWords = JSON.parse(legacyWordsStr);
      if (marbleSavedStateMatchesSubdomain(legacyWords, subdomain)) {
        puzzleWordsStr = legacyWordsStr;
        availableLettersStr = legacyLettersStr;
        localStorage.setItem(keys.puzzleWords, legacyWordsStr);
        localStorage.setItem(keys.availableLetters, legacyLettersStr);
      }
    }
  }

  return {
    keys,
    savedPuzzleWords: puzzleWordsStr ? JSON.parse(puzzleWordsStr) : null,
    savedAvailableLetters: availableLettersStr
      ? JSON.parse(availableLettersStr)
      : null,
  };
}

function Marble() {
  const [settings, setSettings] = useState(null);
  const subdomain = useSubdomain();
  const API_URL = process.env.REACT_APP_API_URL;

  const [selectedLetter, setSelectedLetter] = useState(null); // Store both value and ID
  const [puzzleWords, setPuzzleWords] = useState({});
  const [availableLetters, setAvailableLetters] = useState([]);
  const [isBorderVisible, setIsBorderVisible] = useState(false);
  const [isFirstLetterPlaced, setIsFirstLetterPlaced] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialPuzzleWords, setInitialPuzzleWords] = useState({});
  const [initialAvailableLetters, setInitialAvailableLetters] = useState([]);

  useEffect(() => {
    const { keys: storageKeys, savedPuzzleWords, savedAvailableLetters } =
      loadMarbleSavedState(subdomain);
    const savedSettingsStr = localStorage.getItem("settings");
    const savedSettings = savedSettingsStr ? JSON.parse(savedSettingsStr) : null;

    const subKey = subdomain === "seattle" ? "seattle" : "app";
    let useSaved =
      savedPuzzleWords &&
      savedAvailableLetters &&
      marbleSavedStateMatchesSubdomain(savedPuzzleWords, subdomain);

    if (useSaved) {
      console.log("Setting words from localStorage...");
      setPuzzleWords(savedPuzzleWords);
      setSettings(savedSettings);
      
      // Get subdomain-specific wardrobe
      const wardrobeKey = subdomain === "seattle" ? "seattleWardrobe" : "appWardrobe";
      const currentWardrobe = (savedSettings && savedSettings[wardrobeKey]) ? savedSettings[wardrobeKey] : "Jeans";

      const { words: initialWords, letters: initialLetters } =
        getMarbleInitialPuzzle(subdomain, currentWardrobe);
      setInitialPuzzleWords(initialWords);
      setInitialAvailableLetters(initialLetters);

      const normalizedLetters = normalizeAvailableLetters(
        savedAvailableLetters,
        initialLetters
      );
      setAvailableLetters(normalizedLetters);

      const letterPoolsMatchSaved = (pools) =>
        pools.some(
          (pool) => JSON.stringify(pool) === JSON.stringify(savedAvailableLetters)
        );
      if (!letterPoolsMatchSaved(marbleInitialLetterPoolsBySubdomain[subKey])) {
        setIsFirstLetterPlaced(true);
      }

      const objectsAreEqual = (obj1, obj2) => {
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);
        if (keys1.length !== keys2.length) return false;
        return keys1.every((key) => obj1[key] === obj2[key]);
      };
      if (
        marbleInitialWordSetsBySubdomain[subKey].some((set) =>
          objectsAreEqual(savedPuzzleWords, set)
        )
      ) {
        setIsFirstLetterPlaced(false);
      }

      setIsInitialized(true);
    } else {
      console.log("Fetching settings...");
      fetch(`${API_URL}/api/settings`)
        .then((res) => res.json())
        .then((data) => {
          // Determine which activeActors key to use based on subdomain
          const activeActorsKey = subdomain === "seattle" ? "activeActorsSeattle" : "activeActorsApp";
          
          // Initialize subdomain-specific data if it doesn't exist
          if (!Array.isArray(data[activeActorsKey])) {
            data[activeActorsKey] = [];
          }
          
          // Get subdomain-specific wardrobe
          const wardrobeKey = subdomain === "seattle" ? "seattleWardrobe" : "appWardrobe";
          
          // Initialize subdomain-specific wardrobe if it doesn't exist
          if (!data[wardrobeKey]) {
            data[wardrobeKey] = "Jeans";
          }
          
          const currentWardrobe = data[wardrobeKey];
          
          setSettings(data);

          const { words, letters } = getMarbleInitialPuzzle(subdomain, currentWardrobe);

          setPuzzleWords(words);
          setAvailableLetters(letters);
          localStorage.setItem(storageKeys.puzzleWords, JSON.stringify(words));
          localStorage.setItem(
            storageKeys.availableLetters,
            JSON.stringify(letters)
          );
          localStorage.setItem("settings", JSON.stringify(data));
          setInitialPuzzleWords(words);
          setInitialAvailableLetters(letters);
          setIsInitialized(true);
        });
    }
  }, [API_URL, subdomain]);

  // Helper function to get current wardrobe based on subdomain
  const getCurrentWardrobe = () => {
    if (!settings) return "Jeans";
    const wardrobeKey = subdomain === "seattle" ? "seattleWardrobe" : "appWardrobe";
    return settings[wardrobeKey] || "Jeans";
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setIsBorderVisible((prev) => !prev); // Toggle border visibility
    }, 1000); // 1-second interval

    return () => clearInterval(interval); // Clean up interval on component unmount
  }, []);

  useEffect(() => {
    if (isInitialized) {
      const { puzzleWords: puzzleKey, availableLetters: lettersKey } =
        getMarbleStorageKeys(subdomain);
      localStorage.setItem(puzzleKey, JSON.stringify(puzzleWords));
      localStorage.setItem(lettersKey, JSON.stringify(availableLetters));
    }
  }, [puzzleWords, availableLetters, isInitialized, subdomain]);

  function handleLetterSelect(e) {
    e.preventDefault();
    const { value, dataset } = e.target;
    if (!value) return;
    const id = parseInt(dataset.id, 10);
    setSelectedLetter({ letter: value, id }); // Track the letter and its ID
  }

  function handleLetterPlacement(e) {
    e.preventDefault();
    const { word, index } = e.target.dataset; // Access word and index dynamically
    const wordIndex = parseInt(index, 10); // Convert to a number

    if (word && selectedLetter) {
      setPuzzleWords((prevWords) => {
        const currentWord = prevWords[word];
        // Prevent altering already-placed letters
        if (currentWord[wordIndex] !== "_") {
          return prevWords; // Return without changes
        }

        // Update the word if the slot is empty
        const updatedWord =
          currentWord.slice(0, wordIndex) +
          selectedLetter.letter +
          currentWord.slice(wordIndex + 1);

        return {
          ...prevWords,
          [word]: updatedWord,
        };
      });

      // Leave an empty slot where the letter was (fixed pool layout)
      setAvailableLetters((prevLetters) =>
        prevLetters.map((item) =>
          item.id === selectedLetter.id ? { ...item, letter: null } : item
        )
      );

      setSelectedLetter(null); // Clear the selected letter

      setIsFirstLetterPlaced(true);
    }
  }

  function handleLetterRemoval(e) {
    e.preventDefault();
    const { word, index } = e.target.dataset; // Access the word and index
    const wordIndex = parseInt(index, 10); // Convert index to a number

    setPuzzleWords((prevWords) => {
      const currentWord = prevWords[word];
      const letterToRemove = currentWord[wordIndex]; // Get the letter to remove

      // If the slot is empty, do nothing
      if (letterToRemove === "_") {
        return prevWords;
      }

      // Update the word by replacing the letter with an underscore
      const updatedWord =
        currentWord.slice(0, wordIndex) +
        "_" +
        currentWord.slice(wordIndex + 1);

      // Restore the letter into its original pool slot (empty slot with matching id)
      setAvailableLetters((prevLetters) => {
        const usedIds = new Set(
          prevLetters
            .filter((slot) => slot.letter != null && slot.letter !== "")
            .map((slot) => slot.id)
        );

        const match = initialAvailableLetters.find(
          (initSlot) =>
            initSlot.letter === letterToRemove && !usedIds.has(initSlot.id)
        );

        if (match) {
          return prevLetters.map((item) =>
            item.id === match.id
              ? { letter: letterToRemove, id: match.id }
              : item
          );
        }

        return prevLetters;
      });

      return {
        ...prevWords,
        [word]: updatedWord,
      };
    });
  }

  // function resetUnsolvedWords() {
  //   setPuzzleWords((prevWords) => {
  //     const resetWords = {};
  //     const usedLetters = []; // Track letters in unsolved words for resetting

  //     for (const [word, value] of Object.entries(prevWords)) {
  //       if (value !== word.toUpperCase()) {
  //         // Reset only unsolved words and collect used letters
  //         const initialWord = initialPuzzleWords[word];
  //         resetWords[word] = initialWord;

  //         // Collect letters from the unsolved word
  //         for (let i = 0; i < value.length; i++) {
  //           if (value[i] !== "_" && initialWord[i] === "_") {
  //             usedLetters.push(value[i]);
  //           }
  //         }
  //       } else {
  //         resetWords[word] = value; // Keep solved words as they are
  //       }
  //     }

  //     // Merge unsolved word letters into available letters
  //     setAvailableLetters((prevAvailable) => {
  //       // setrack the ids of letters that have been added to resetLetters
  //       const addedIds = new Set(prevAvailable.map((a) => a.id));

  //       const resetLetters = usedLetters
  //         .map((letter) => {
  //           // Find a unique ID for each used letter, ensuring we haven't already added that ID
  //           const match = initialAvailableLetters.find(
  //             (l) =>
  //               l.letter === letter &&
  //               !addedIds.has(l.id) && // Check if this id has already been added
  //               !prevAvailable.some((a) => a.id === l.id) // Also check if the id exists in prevAvailable
  //           );

  //           if (match) {
  //             // Add the match id to the addedIds set
  //             addedIds.add(match.id);
  //           }

  //           return match || null; // Ensure we only add valid matches
  //         })
  //         .filter(Boolean); // Remove nulls

  //       console.log("reset letters: ", resetLetters);
  //       console.log("available letters: ", availableLetters);
  //       return [...prevAvailable, ...resetLetters];
  //     });

  //     return resetWords;
  //   });
  // }

  function areAllWordsSolved() {
    return Object.entries(puzzleWords).every(([word, value]) => {
      if (word === "starbucks") return value === "★-BUCKS";
      return value === word.toUpperCase();
    });
  }

  // TEMPORARY
  // function resetGame() {
  //   setAvailableLetters(initialAvailableLetters)
  //   setPuzzleWords(initialPuzzleWords)
  //   setIsFirstLetterPlaced(false)
  //   setSelectedLetter(null)
  // }

  // function solveGame(){
  //   setAvailableLetters([])
  //   setIsFirstLetterPlaced(true)
  //   setPuzzleWords({
  //     swan: "SWAN",
  //     necklace: "NECKLACE",
  //     marble: "MARBLE",
  //     danger: "DANGER",
  //     signal: "SIGNAL",
  //     vui: "VUI",
  //     ve: "VE",
  //     cup: "CUP",
  //     blue: "BLUE",
  //     jeans: "JEANS",
  //     black: "BLACK",
  //     hat: "HAT",
  //   })
  // }
  // TEMPORARY

  if (!isInitialized) return <div>Loading...</div>;

  if (isInitialized)
    return (
      <div className="pageContent" style={{ paddingBottom: "0vw" }}>
        <title>Agent Marble Physical Description</title>
        <VisitedPagesMenu />
        <div className="orangeBar" style={{ height: "13vw" }}>
          Agent Marble is ...
        </div>

        {areAllWordsSolved() ? (
          <div className="marbleInstructions">SUCCESS!</div>
        ) : null}

        {areAllWordsSolved() ? (
          <div
            className="marbleCompletionMessage"
            style={{
              border: isBorderVisible
                ? "2px solid red"
                : "2px solid transparent", // Apply border conditionally
              padding: "10px", // Optional: Adjust spacing for better appearance
            }}
          >
            NOW TEXT PAPYRUS
            <br />
            "READY FOR ACTION"
          </div>
        ) : (
          <div className="lettersContainer">
            {availableLetters.map((item, index) =>
              item.letter == null || item.letter === "" ? (
                <button
                  key={`${item.id}-empty-${index}`}
                  type="button"
                  disabled
                  className="marbleButton"
                  aria-hidden={true}
                  style={{
                    visibility: "hidden",
                    pointerEvents: "none",
                  }}
                >
                  {"\u00A0"}
                </button>
              ) : (
                <button
                  key={`${item.id}-${item.letter}-${index}`}
                  type="button"
                  value={item.letter}
                  data-id={item.id}
                  onClick={handleLetterSelect}
                  className="marbleButton"
                  style={{
                    backgroundColor:
                      selectedLetter?.id === item.id ? "#FF3700" : "white",
                    color: selectedLetter?.id === item.id ? "white" : "black",
                  }}
                >
                  {item.letter}
                </button>
              )
            )}
          </div>
        )}

        <div className="puzzleContainer">
          <div className="marbleUpperFlex">
            <div className="swanNecklaceContainer">
              <div
                className={
                  puzzleWords.swan === "SWAN"
                    ? // || puzzleWords.necklace === "NECKLACE"
                      "marbleIsSolved"
                    : "marbleIs"
                }
              >
                ...wearing a...
              </div>

              {/* Render buttons for SWAN */}
              {puzzleWords.swan.split("").map((char, index) => {
                const isLockedLetter = initialPuzzleWords.swan[index] !== "_"; // Check if the letter was pre-provided
                const wordIsSolved = puzzleWords.swan === "SWAN";
                return (
                  <button
                    key={`swan-${index}`}
                    data-word="swan"
                    data-index={index}
                    onClick={
                      char === "_" ? handleLetterPlacement : handleLetterRemoval
                    }
                    disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                    className={
                      wordIsSolved
                        ? "solvedWordLetter" // Highest priority when word is solved
                        : isLockedLetter
                        ? "lockedLetter" // Pre-provided letters
                        : "marbleButton" // Default for user-interactable letters
                    }
                    // style={{
                    //   border: "none", // remove border
                    //   margin: "2px", // remove border
                    //   width: "6vw", // remove border
                    //   height: "6vw", // remove border
                    //   paddingTop: "1px", // remove border
                    // }}
                  >
                    {char === "_" ? "\u00A0" : char}
                  </button>
                );
              })}

              <br />

              {/* Render buttons for NECKLACE */}
              {puzzleWords.necklace.split("").map((char, index) => {
                const isLockedLetter =
                  initialPuzzleWords.necklace[index] !== "_"; // Check if the letter was pre-provided
                const wordIsSolved = puzzleWords.necklace === "NECKLACE";
                return (
                  <button
                    key={`necklace-${index}`}
                    data-word="necklace"
                    data-index={index}
                    onClick={
                      char === "_" ? handleLetterPlacement : handleLetterRemoval
                    }
                    disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                    className={
                      wordIsSolved
                        ? "solvedWordLetter" // Highest priority when word is solved
                        : isLockedLetter
                        ? "lockedLetter" // Pre-provided letters
                        : "marbleButton" // Default for user-interactable letters
                    }
                  >
                    {char === "_" ? "\u00A0" : char}
                  </button>
                );
              })}
            </div>

            <div className="blackHatContainer">
              <div
                className={
                  puzzleWords.black === "BLACK"
                    ? // || puzzleWords.hat === "HAT"
                      "marbleIsSolved"
                    : "marbleIs"
                }
              >
                ...wearing a...
              </div>

              {/* Render buttons for BLACK */}
              {puzzleWords.black.split("").map((char, index) => {
                const isLockedLetter = initialPuzzleWords.black[index] !== "_"; // Check if the letter was pre-provided
                const wordIsSolved = puzzleWords.black === "BLACK";
                return (
                  <button
                    key={`black-${index}`}
                    data-word="black"
                    data-index={index}
                    onClick={
                      char === "_" ? handleLetterPlacement : handleLetterRemoval
                    }
                    disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                    className={
                      wordIsSolved
                        ? "solvedWordLetter" // Highest priority when word is solved
                        : isLockedLetter
                        ? "lockedLetter" // Pre-provided letters
                        : "marbleButton" // Default for user-interactable letters
                    }
                  >
                    {char === "_" ? "\u00A0" : char}
                  </button>
                );
              })}

              <br />

              <button className="lockedLetter"></button>

              {/* Render buttons for HAT */}
              {puzzleWords.hat.split("").map((char, index) => {
                const isLockedLetter = initialPuzzleWords.hat[index] !== "_"; // Check if the letter was pre-provided
                const wordIsSolved = puzzleWords.hat === "HAT";
                return (
                  <button
                    key={`hat-${index}`}
                    data-word="hat"
                    data-index={index}
                    onClick={
                      char === "_" ? handleLetterPlacement : handleLetterRemoval
                    }
                    disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                    className={
                      wordIsSolved
                        ? "solvedWordLetter" // Highest priority when word is solved
                        : isLockedLetter
                        ? "lockedLetter" // Pre-provided letters
                        : "marbleButton" // Default for user-interactable letters
                    }
                  >
                    {char === "_" ? "\u00A0" : char}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="marbleLowerFlex">
            <div className="leftSideContainer">
              <div
                className={
                  puzzleWords.marble === "MARBLE"
                    ? // || puzzleWords.danger === "DANGER"
                      // || puzzleWords.signal === "SIGNAL"
                      "marbleIsSolved"
                    : "marbleIs"
                }
              >
                ...showing the...
              </div>

              {/* Render buttons for MARBLE */}
              {puzzleWords.marble.split("").map((char, index) => {
                const isLockedLetter = initialPuzzleWords.marble[index] !== "_"; // Check if the letter was pre-provided
                const wordIsSolved = puzzleWords.marble === "MARBLE";
                return (
                  <button
                    key={`marble-${index}`}
                    data-word="marble"
                    data-index={index}
                    onClick={
                      char === "_" ? handleLetterPlacement : handleLetterRemoval
                    }
                    disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                    className={
                      wordIsSolved
                        ? "solvedWordLetter" // Highest priority when word is solved
                        : isLockedLetter
                        ? "lockedLetter" // Pre-provided letters
                        : "marbleButton" // Default for user-interactable letters
                    }
                  >
                    {char === "_" ? "\u00A0" : char}
                  </button>
                );
              })}

              <br />

              {/* Render buttons for DANGER */}
              {puzzleWords.danger.split("").map((char, index) => {
                const isLockedLetter = initialPuzzleWords.danger[index] !== "_"; // Check if the letter was pre-provided
                const wordIsSolved = puzzleWords.danger === "DANGER";
                return (
                  <button
                    key={`danger-${index}`}
                    data-word="danger"
                    data-index={index}
                    onClick={
                      char === "_" ? handleLetterPlacement : handleLetterRemoval
                    }
                    disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                    className={
                      wordIsSolved
                        ? "solvedWordLetter" // Highest priority when word is solved
                        : isLockedLetter
                        ? "lockedLetter" // Pre-provided letters
                        : "marbleButton" // Default for user-interactable letters
                    }
                  >
                    {char === "_" ? "\u00A0" : char}
                  </button>
                );
              })}

              <br />

              {/* Render buttons for SIGNAL */}
              {puzzleWords.signal.split("").map((char, index) => {
                const isLockedLetter = initialPuzzleWords.signal[index] !== "_"; // Check if the letter was pre-provided
                const wordIsSolved = puzzleWords.signal === "SIGNAL";
                return (
                  <button
                    key={`signal-${index}`}
                    data-word="signal"
                    data-index={index}
                    onClick={
                      char === "_" ? handleLetterPlacement : handleLetterRemoval
                    }
                    disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                    className={
                      wordIsSolved
                        ? "solvedWordLetter" // Highest priority when word is solved
                        : isLockedLetter
                        ? "lockedLetter" // Pre-provided letters
                        : "marbleButton" // Default for user-interactable letters
                    }
                  >
                    {char === "_" ? "\u00A0" : char}
                  </button>
                );
              })}

              <br />

              <div
                className={
                  subdomain === "seattle"
                    ? puzzleWords.starbucks === "★-BUCKS"
                      ? "marbleIsSolved"
                      : "marbleIs"
                    : puzzleWords.vui === "VUI"
                    ? // || puzzleWords.ve === "VE"
                      // || puzzleWords.cup === "CUP"
                      "marbleIsSolved"
                    : "marbleIs"
                }
              >
                ...holding a...
              </div>

              {subdomain === "seattle" ? (
                <>
                  {(() => {
                    const starbucksValue =
                      puzzleWords.starbucks ??
                      initialPuzzleWords.starbucks ??
                      "";
                    const wordIsSolved = starbucksValue === "★-BUCKS";
                    const displayWord = wordIsSolved
                      ? "STARBUCKS"
                      : starbucksValue;

                    const letters = displayWord.split("").map((char, index) => {
                      const isLockedLetter =
                        !wordIsSolved &&
                        initialPuzzleWords.starbucks[index] !== "_";
                      return (
                        <button
                          key={`starbucks-${index}`}
                          data-word="starbucks"
                          data-index={index}
                          onClick={
                            char === "_"
                              ? handleLetterPlacement
                              : handleLetterRemoval
                          }
                          disabled={isLockedLetter || wordIsSolved}
                          className={
                            wordIsSolved
                              ? "solvedStarbucksLetter"
                              : isLockedLetter
                              ? "lockedLetter"
                              : "marbleButton"
                          }
                        >
                          {char === "_" ? "\u00A0" : char}
                        </button>
                      );
                    });

                    return wordIsSolved ? (
                      <span className="solvedStarbucksWord">{letters}</span>
                    ) : (
                      letters
                    );
                  })()}
                </>
              ) : (
                <>
                  {/* Render buttons for VUI */}
                  {puzzleWords.vui.split("").map((char, index) => {
                    const isLockedLetter = initialPuzzleWords.vui[index] !== "_"; // Check if the letter was pre-provided
                    const wordIsSolved = puzzleWords.vui === "VUI";
                    return (
                      <button
                        key={`vui-${index}`}
                        data-word="vui"
                        data-index={index}
                        onClick={
                          char === "_" ? handleLetterPlacement : handleLetterRemoval
                        }
                        disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                        className={
                          wordIsSolved
                            ? "solvedWordLetter" // Highest priority when word is solved
                            : isLockedLetter
                            ? "lockedLetter" // Pre-provided letters
                            : "marbleButton" // Default for user-interactable letters
                        }
                      >
                        {char === "_" ? "\u00A0" : char}
                      </button>
                    );
                  })}

                  <button className="lockedLetter"></button>

                  {/* Render buttons for VE */}
                  {puzzleWords.ve.split("").map((char, index) => {
                    const isLockedLetter = initialPuzzleWords.ve[index] !== "_"; // Check if the letter was pre-provided
                    const wordIsSolved = puzzleWords.ve === "VE";
                    return (
                      <button
                        key={`ve-${index}`}
                        data-word="ve"
                        data-index={index}
                        onClick={
                          char === "_" ? handleLetterPlacement : handleLetterRemoval
                        }
                        disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                        className={
                          wordIsSolved
                            ? "solvedWordLetter" // Highest priority when word is solved
                            : isLockedLetter
                            ? "lockedLetter" // Pre-provided letters
                            : "marbleButton" // Default for user-interactable letters
                        }
                      >
                        {char === "_" ? "\u00A0" : char}
                      </button>
                    );
                  })}
                </>
              )}

              <br />

              {/* Render buttons for CUP */}
              {puzzleWords.cup.split("").map((char, index) => {
                const isLockedLetter = initialPuzzleWords.cup[index] !== "_"; // Check if the letter was pre-provided
                const wordIsSolved = puzzleWords.cup === "CUP";
                return (
                  <button
                    key={`cup-${index}`}
                    data-word="cup"
                    data-index={index}
                    onClick={
                      char === "_" ? handleLetterPlacement : handleLetterRemoval
                    }
                    disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                    className={
                      wordIsSolved
                        ? "solvedWordLetter" // Highest priority when word is solved
                        : isLockedLetter
                        ? "lockedLetter" // Pre-provided letters
                        : "marbleButton" // Default for user-interactable letters
                    }
                  >
                    {char === "_" ? "\u00A0" : char}
                  </button>
                );
              })}

              <div
                className={
                  (getCurrentWardrobe() === "Jeans" &&
                    puzzleWords.blue === "BLUE") ||
                  // || puzzleWords.jeans === "JEANS"
                  (getCurrentWardrobe() === "Shorts" &&
                    puzzleWords.shorts === "SHORTS")
                    ? "marbleIsSolved"
                    : "marbleIs"
                }
              >
                ...wearing...
              </div>

              {/* Render buttons for SHORTS */}
              {getCurrentWardrobe() === "Shorts" &&
                puzzleWords.shorts &&
                puzzleWords.shorts.split("").map((char, index) => {
                  const isLockedLetter =
                    initialPuzzleWords.shorts && initialPuzzleWords.shorts[index] !== "_"; // Check if the letter was pre-provided
                  const wordIsSolved = puzzleWords.shorts === "SHORTS";
                  return (
                    <button
                      key={`shorts-${index}`}
                      data-word="shorts"
                      data-index={index}
                      onClick={
                        char === "_"
                          ? handleLetterPlacement
                          : handleLetterRemoval
                      }
                      disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                      className={
                        wordIsSolved
                          ? "solvedWordLetter" // Highest priority when word is solved
                          : isLockedLetter
                          ? "lockedLetter" // Pre-provided letters
                          : "marbleButton" // Default for user-interactable letters
                      }
                    >
                      {char === "_" ? "\u00A0" : char}
                    </button>
                  );
                })}

              {/* Render buttons for BLUE */}
              {getCurrentWardrobe() === "Jeans" &&
                puzzleWords.blue &&
                puzzleWords.blue.split("").map((char, index) => {
                  const isLockedLetter = initialPuzzleWords.blue && initialPuzzleWords.blue[index] !== "_"; // Check if the letter was pre-provided
                  const wordIsSolved = puzzleWords.blue === "BLUE";
                  return (
                    <button
                      key={`blue-${index}`}
                      data-word="blue"
                      data-index={index}
                      onClick={
                        char === "_"
                          ? handleLetterPlacement
                          : handleLetterRemoval
                      }
                      disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                      className={
                        wordIsSolved
                          ? "solvedWordLetter" // Highest priority when word is solved
                          : isLockedLetter
                          ? "lockedLetter" // Pre-provided letters
                          : "marbleButton" // Default for user-interactable letters
                      }
                    >
                      {char === "_" ? "\u00A0" : char}
                    </button>
                  );
                })}

              <br />

              {/* Render buttons for JEANS */}
              {getCurrentWardrobe() === "Jeans" &&
                puzzleWords.jeans &&
                puzzleWords.jeans.split("").map((char, index) => {
                  const isLockedLetter =
                    initialPuzzleWords.jeans && initialPuzzleWords.jeans[index] !== "_"; // Check if the letter was pre-provided
                  const wordIsSolved = puzzleWords.jeans === "JEANS";
                  return (
                    <button
                      key={`jeans-${index}`}
                      data-word="jeans"
                      data-index={index}
                      onClick={
                        char === "_"
                          ? handleLetterPlacement
                          : handleLetterRemoval
                      }
                      disabled={isLockedLetter || wordIsSolved} // Prevent interaction for locked letters
                      className={
                        wordIsSolved
                          ? "solvedWordLetter" // Highest priority when word is solved
                          : isLockedLetter
                          ? "lockedLetter" // Pre-provided letters
                          : "marbleButton" // Default for user-interactable letters
                      }
                    >
                      {char === "_" ? "\u00A0" : char}
                    </button>
                  );
                })}
            </div>

            <div className="yellowManContainer">
              <img src={yellowMan} className="yellowMan" alt="walking man" />
              {isFirstLetterPlaced ? null : (
                <div className="marblePuzzleInstructions">
                  Tap a letter above, then tap the space where it belongs
                </div>
              )}
            </div>
          </div>

          <br />

          {/* {areAllWordsSolved() ? (
          <div className="marbleInstructions">SUCCESS!</div>
        ) : isFirstLetterPlaced ? <div className="marbleInstructions"><br/><br/></div> : (
          <div className="marbleInstructions">
            Tap a letter below and then tap the space where it belongs above!
          </div>
        )} */}

          <br />

          {/* HERE IS THE RESET BUTTON IF WE NEED TO PUT IT BACK IN - ALSO NEED TO UNCOMMENT THE resetUnsolvedWords FUNCTION ABOVE */}
          {/* {areAllWordsSolved() ? null : (
          <div className="resetButtonContainer">
            <button onClick={resetUnsolvedWords} className="resetButton">
              Reset Unsolved Words
            </button>
          </div>
        )} */}

          {/* temporary */}
          {/* <div className="resetButtonContainer">
          <button onClick={resetGame} className="resetButton">
            TEMP Reset Game
          </button>
          <button onClick={solveGame} className="resetButton">
            TEMP Solve Game
          </button>
        </div> */}
          {/* temporary */}
        </div>

        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <LeslieFooter unfixed={true} />
      </div>
    );
}

export default Marble;
